import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { ClipboardCheck, Package, CalendarClock, Thermometer, Trash2, AlertTriangle, Loader2, Bell } from "lucide-react";
import { AiAssistantPanel } from "@/components/AiAssistant";
import { toast } from "sonner";

function StatusCard({ testid, icon: Icon, title, value, sub, tone, onClick }) {
  const tones = {
    ok: "border-emerald-200 bg-emerald-50/60",
    warn: "border-amber-200 bg-amber-50/60",
    crit: "border-red-200 bg-red-50/60",
    neutral: "border-border bg-card",
  };
  const iconTones = {
    ok: "bg-emerald-600", warn: "bg-amber-600", crit: "bg-red-600", neutral: "bg-sky-600",
  };
  return (
    <button data-testid={testid} onClick={onClick}
      className={`tactile-btn text-left rounded-2xl border-2 p-4 sm:p-5 ${tones[tone]} w-full`}>
      <div className="flex items-start justify-between gap-2">
        <div className={`h-10 w-10 rounded-xl ${iconTones[tone]} flex items-center justify-center shrink-0`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <span className="font-display text-2xl sm:text-3xl font-extrabold">{value}</span>
      </div>
      <p className="mt-3 font-semibold text-sm">{title}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </button>
  );
}

export default function DashboardPage() {
  const { locationId } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);

  const refresh = useCallback(() => {
    api.get("/dashboard", { params: { location_id: locationId } })
      .then((r) => setDash(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [locationId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("sk:data-changed", h);
    return () => window.removeEventListener("sk:data-changed", h);
  }, [refresh]);

  if (!dash) {
    return <div className="flex justify-center py-24" data-testid="dashboard-loading"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const stockTone = dash.stock.out > 0 ? "crit" : dash.stock.critical > 0 ? "warn" : "ok";
  const tempTone = dash.temperatures.out_of_range > 0 ? "crit" : "ok";
  const haccpTone = dash.haccp.pending.length > 0 ? "warn" : "ok";
  const expTone = (dash.expirations.expired + dash.expirations.today) > 0 ? "crit"
    : (dash.expirations.tomorrow + dash.expirations["3days"]) > 0 ? "warn" : "ok";
  const ncTone = dash.nonconformities.open > 0 ? "warn" : "ok";

  return (
    <div className="space-y-6 md:pl-14" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("dash.today")} — {dash.location?.name}</p>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold">Dashboard</h1>
        </div>
        {dash.trial && !dash.plan && (
          <button data-testid="trial-badge" onClick={() => navigate("/prezzi")}
            className={`tactile-btn text-xs font-bold px-4 py-2.5 rounded-full border-2 ${dash.trial.active ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}>
            {dash.trial.active
              ? `${t("pay.trialActive")} · ${dash.trial.days_left} ${t("pay.trialDaysLeft")} · ${t("pay.trialFree")}`
              : t("pay.trialExpired")}
          </button>
        )}
      </div>

      <AiAssistantPanel />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="status-cards-grid">
        <StatusCard testid="status-card-haccp" icon={ClipboardCheck} title={t("dash.haccpControls")}
          value={`${dash.haccp.completed_today}/${dash.haccp.due}`}
          sub={dash.haccp.pending.length ? `${dash.haccp.pending.length} ${t("dash.pending")}` : t("dash.allOk")}
          tone={haccpTone} onClick={() => navigate("/haccp")} />
        <StatusCard testid="status-card-stock" icon={Package} title={t("dash.criticalStock")}
          value={dash.stock.critical + dash.stock.out}
          sub={`${t("dash.inventoryValue")}: €${dash.stock.inventory_value.toLocaleString()}`}
          tone={stockTone} onClick={() => navigate("/magazzino")} />
        <StatusCard testid="status-card-expirations" icon={CalendarClock} title={t("dash.expiring")}
          value={dash.expirations.expired + dash.expirations.today + dash.expirations.tomorrow}
          sub={`${dash.expirations.expired} ${t("dash.expired")} · ${dash.expirations.today} ${t("dash.expiringToday")}`}
          tone={expTone} onClick={() => navigate("/scadenze")} />
        <StatusCard testid="status-card-temperatures" icon={Thermometer} title={t("dash.temperatures")}
          value={`${dash.temperatures.total - dash.temperatures.out_of_range}/${dash.temperatures.total}`}
          sub={dash.temperatures.out_of_range ? `${dash.temperatures.out_of_range} ${t("dash.outOfRange")}` : t("dash.allOk")}
          tone={tempTone} onClick={() => navigate("/temperature")} />
        <StatusCard testid="status-card-waste" icon={Trash2} title={t("dash.wasteToday")}
          value={`€${dash.waste.today_cost.toFixed(2)}`}
          sub={`${t("dash.wasteMonth")}: €${dash.waste.month_cost.toFixed(2)}`}
          tone={dash.waste.today_cost > 0 ? "warn" : "ok"} onClick={() => navigate("/sprechi")} />
        <StatusCard testid="status-card-nonconformities" icon={AlertTriangle} title={t("dash.openNc")}
          value={dash.nonconformities.open}
          sub={dash.nonconformities.open ? t("dash.attention") : t("dash.allOk")}
          tone={ncTone} onClick={() => navigate("/non-conformita")} />
      </div>

      <div data-testid="quick-actions" className="space-y-3">
        <h2 className="font-display font-bold text-lg">{t("dash.quickActions")}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { key: "actions.recordTemp", icon: Thermometer, to: "/temperature", tid: "quick-action-temp-button", cls: "bg-cyan-600" },
            { key: "actions.loadGoods", icon: Package, to: "/magazzino", tid: "quick-action-load-button", cls: "bg-emerald-600" },
            { key: "actions.logWaste", icon: Trash2, to: "/sprechi", tid: "quick-action-waste-button", cls: "bg-orange-600" },
            { key: "actions.viewExpirations", icon: CalendarClock, to: "/scadenze", tid: "quick-action-expirations-button", cls: "bg-amber-600" },
            { key: "actions.haccpCheck", icon: ClipboardCheck, to: "/haccp", tid: "quick-action-haccp-button", cls: "bg-violet-600" },
          ].map(({ key, icon: Icon, to, tid, cls }) => (
            <button key={key} data-testid={tid} onClick={() => navigate(to)}
              className="tactile-btn bg-card border rounded-2xl p-4 flex flex-col items-center gap-2 min-h-24 justify-center">
              <div className={`h-10 w-10 rounded-xl ${cls} flex items-center justify-center`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <span className="text-xs font-semibold text-center leading-tight">{t(key)}</span>
            </button>
          ))}
        </div>
      </div>

      {dash.haccp.pending.length > 0 && (
        <div className="bg-card border rounded-2xl p-4" data-testid="pending-haccp-list">
          <h3 className="font-display font-semibold text-sm mb-2 flex items-center gap-2">
            <Bell className="h-4 w-4 text-amber-600" />
            {t("dash.haccpControls")} {t("dash.pending")}
          </h3>
          <div className="space-y-1.5">
            {dash.haccp.pending.map((tpl) => (
              <button key={tpl.template_id} data-testid={`pending-check-${tpl.template_id}`}
                onClick={() => navigate("/haccp")}
                className="tactile-btn w-full text-left flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                <span className="text-sm font-medium">{tpl.name}</span>
                <span className="text-[10px] font-bold uppercase text-amber-700">{tpl.shift}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
